import { useState, useEffect, useMemo } from "react";
import { Maximize2, Eye, EyeOff } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useStepScreenshot } from "@/hooks/use-step-screenshot";
import { cn } from "@/lib/utils";
import {
  type HighlightTone,
  type ScreenshotHighlight,
  type TargetBox,
  isValidBox,
} from "./screenshot-types";
import { ScreenshotSvg } from "./screenshot-svg";
import { ZoomedScreenshotView } from "./zoomed-screenshot-view";

export * from "./screenshot-types";
export * from "./screenshot-svg";
export * from "./step-thumbnail";
export * from "./zoomed-screenshot-view";

/**
 * A walkthrough step's screenshot, with loading and empty states, zoom modal,
 * and unified SVG highlight overlay.
 */
export function StepScreenshot({
  walkthroughId,
  stepNumber,
  alt,
  className,
  targetBox,
  targetTone,
  highlights,
  activeHighlightId,
  onSelectHighlight,
  enableZoom = true,
  onOpenZoom,
}: {
  walkthroughId: string | null | undefined;
  stepNumber: number | null | undefined;
  alt: string;
  /** Classes for the container */
  className?: string;
  /** Optional bounding box of the problem area to highlight */
  targetBox?: TargetBox | null;
  /** Optional tone/severity for the single targetBox */
  targetTone?: HighlightTone | string | null;
  /** Optional multiple highlights on this step */
  highlights?: ScreenshotHighlight[];
  /** Currently active/focused highlight */
  activeHighlightId?: string | null;
  /** Callback when a highlight box is clicked */
  onSelectHighlight?: (id: string) => void;
  /** Whether clicking the image opens a full-screen zoom modal (default true) */
  enableZoom?: boolean;
  /** Custom zoom handler (e.g. for timeline gallery navigation) */
  onOpenZoom?: (stepNumber: number) => void;
}) {
  const { url, loading } = useStepScreenshot(walkthroughId, stepNumber);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [showHighlights, setShowHighlights] = useState(true);
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const activeHighlights = useMemo(() => {
    if (highlights && highlights.length > 0) return highlights;
    if (isValidBox(targetBox)) {
      return [{ id: "target", box: targetBox, tone: targetTone }];
    }
    return [];
  }, [highlights, targetBox, targetTone]);

  useEffect(() => {
    if (!url) return;
    const img = new Image();
    img.src = url;
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setNaturalSize({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      }
    };
  }, [url]);

  // Global keyboard shortcut (H) to toggle highlights
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (zoomOpen) return;
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        return;
      }
      if (e.key === "h" || e.key === "H") {
        setShowHighlights((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [zoomOpen]);

  if (loading) {
    return <Skeleton className={cn("aspect-video w-full", className)} />;
  }
  if (!url || typeof stepNumber !== "number" || !walkthroughId) {
    return (
      <div
        className={cn(
          "flex aspect-video w-full items-center justify-center bg-muted/20 text-xs text-muted-foreground",
          className,
        )}
      >
        <span>No screenshot</span>
      </div>
    );
  }

  const handleOpenZoom = () => {
    if (onOpenZoom) {
      onOpenZoom(stepNumber);
    } else {
      setZoomOpen(true);
    }
  };

  const hasControls = activeHighlights.length > 0 || enableZoom;

  return (
    <>
      <div
        className={cn(
          "group relative flex h-full w-full items-center justify-center overflow-hidden",
          className,
        )}
      >
        {enableZoom ? (
          <div
            role="button"
            tabIndex={0}
            onClick={handleOpenZoom}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleOpenZoom();
              }
            }}
            className="relative flex h-full w-full cursor-zoom-in items-center justify-center overflow-hidden text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            title="Click to enlarge screenshot"
            aria-label={
              alt ? `${alt} — Click to enlarge` : "Click to enlarge screenshot"
            }
          >
            <ScreenshotSvg
              url={url}
              alt={alt}
              highlights={activeHighlights}
              activeHighlightId={activeHighlightId}
              showHighlights={showHighlights}
              onSelectHighlight={onSelectHighlight}
              naturalSize={naturalSize}
              className="h-full w-full object-contain"
            />
          </div>
        ) : (
          <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
            <ScreenshotSvg
              url={url}
              alt={alt}
              highlights={activeHighlights}
              activeHighlightId={activeHighlightId}
              showHighlights={showHighlights}
              onSelectHighlight={onSelectHighlight}
              naturalSize={naturalSize}
              className="h-full w-full object-contain"
            />
          </div>
        )}

        {/* Floating toolbar with accessible sibling buttons */}
        {hasControls && (
          <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
            {activeHighlights.length > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowHighlights((prev) => !prev);
                }}
                className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-xs font-medium text-foreground shadow-xs transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
                title={
                  showHighlights
                    ? "Hide annotations (H)"
                    : "Show annotations (H)"
                }
                aria-label={
                  showHighlights ? "Hide annotations" : "Show annotations"
                }
              >
                {showHighlights ? (
                  <Eye className="h-3 w-3 text-primary" aria-hidden="true" />
                ) : (
                  <EyeOff
                    className="h-3 w-3 text-muted-foreground"
                    aria-hidden="true"
                  />
                )}
                <span>{showHighlights ? "Highlighted" : "Clean"}</span>
                <kbd className="hidden sm:inline-block rounded border border-border bg-muted px-1 font-mono text-xs text-muted-foreground">
                  H
                </kbd>
              </button>
            )}

            {enableZoom && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenZoom();
                }}
                className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-xs font-medium text-foreground shadow-xs transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
                title="Enlarge screenshot"
                aria-label="Enlarge screenshot"
              >
                <Maximize2 className="h-3 w-3" aria-hidden="true" />
                <span>Enlarge</span>
              </button>
            )}
          </div>
        )}
      </div>

      {!onOpenZoom && enableZoom && (
        <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
          <DialogContent className="flex h-[88vh] max-h-[94vh] w-[95vw] max-w-7xl flex-col gap-3 p-4 sm:max-w-7xl">
            <DialogHeader className="border-b pb-2">
              <DialogTitle className="truncate pr-8 text-sm font-semibold">
                {alt || `Step ${stepNumber} screenshot`}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Full resolution walkthrough screenshot for step {stepNumber}
              </DialogDescription>
            </DialogHeader>

            <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-auto rounded bg-muted/30 p-2">
              <ZoomedScreenshotView
                walkthroughId={walkthroughId}
                stepNumber={stepNumber}
                alt={alt}
                targetTone={targetTone}
                highlights={activeHighlights}
                activeHighlightId={activeHighlightId}
                onSelectHighlight={onSelectHighlight}
                initialShowHighlights={showHighlights}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
